import {
  getReadonlyListItemDate,
  getReadonlyListItemId,
  getReadonlyListItemNumber,
  getReadonlyListItemPill,
  getReadonlyListItemTranslatedString,
} from '@interxfi/app-components/components/readonly-list/util';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import type { ReadonlyListItemRecord } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Pill } from '@interxfi/design-system/components/pill/pill';
import type {
  IxcRegistrationV1CipIdentityEvidence,
  IxcRegistrationV1CipIdentityEvidenceVendorData,
  IxcRegistrationV1CipIdentityMinimalEvidence,
  IxcRegistrationV1CipOfacEvidence,
  IxcRegistrationV1CipOfacEvidenceVendorData,
  IxcRegistrationV1CipOfacMinimalEvidence,
  IxcRegistrationV1ClearingManualProfileApprovalEvidence,
  IxcRegistrationV1ClearingProfileEvidence,
  IxcRegistrationV1ProfileCheck,
  IxcRegistrationV1ProfileCheckType,
  IxcRegistrationV1ProfileCheckTypeCipIdentity,
  IxcRegistrationV1ProfileCheckTypeCipIdentityMinimal,
  IxcRegistrationV1ProfileCheckTypeCipOfac,
  IxcRegistrationV1ProfileCheckTypeCipOfacMinimal,
  IxcRegistrationV1ProfileCheckTypeClearingManualProfileApproval,
  IxcRegistrationV1ProfileEvidence,
  IxcRegistrationV1ProfileVersion,
  IxcRegistrationV1ProfileVersionKeys,
  IxcRegistrationV1ProfileVersionState,
} from '@interxfi/types/generated/api';
import { getOneOfType } from '@interxfi/types/helpers/one-of';
import { getNumberOrUndefinedFromNullable } from '@interxfi/utilities/object/access';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import type { ReplaceValuesAndMakeOptional } from '@interxfi/utilities/types/replace';
import { getJ5StateMetadataListItems } from '../../../j5/state/j5-state-list-items';
import { getProgramDetailPath } from '../../../userauth/program/program-routing';
import {
  getRegistrationIndividualProfileListItemSummary,
  getRegistrationOrganizationProfileListItemSummary,
  getRegistrationProfileTypeListItems,
} from '../profile-list-items';
import { getRegistrationProfileDetailPath } from '../registration-profile-routing';
import { ProfileVersionStatusPill } from './profile-version-status-pill';

export function getRegistrationProfileCipIdentityEvidenceVendorDataListItems(
  data: IxcRegistrationV1CipIdentityEvidenceVendorData | undefined,
  prefix = '',
) {
  const typeNameColumn = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixcRegistrationV1',
      oneOfName: 'IxcRegistrationV1CipIdentityEvidenceVendorData',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return typeNameColumn;
  }

  if ('idology' in data) {
    return {
      ...typeNameColumn,
      idology: {
        '!type': 'idology',
        value: {
          id: getReadonlyListItemId(`${prefix}idology.id`, 'ID', data?.idology.id),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('lexisNexis' in data) {
    return {
      ...typeNameColumn,
      lexisNexis: {
        '!type': 'lexisNexis',
        value: {
          id: getReadonlyListItemId(`${prefix}lexisNexis.id`, 'ID', data?.lexisNexis.id),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('plaid' in data) {
    return {
      ...typeNameColumn,
      plaid: {
        '!type': 'plaid',
        value: {
          id: getReadonlyListItemId(`${prefix}plaid.id`, 'ID', data?.plaid.id),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('equifax' in data) {
    return {
      ...typeNameColumn,
      equifax: {
        '!type': 'equifax',
        value: {
          id: getReadonlyListItemId(`${prefix}equifax.id`, 'ID', data?.equifax.id),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('socure' in data) {
    return {
      ...typeNameColumn,
      socure: {
        '!type': 'socure',
        value: {
          id: getReadonlyListItemId(`${prefix}socure.id`, 'ID', data?.socure.id),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('idMe' in data) {
    return {
      ...typeNameColumn,
      idMe: {
        '!type': 'idMe',
        value: {
          id: getReadonlyListItemId(`${prefix}idMe.id`, 'ID', data?.idMe.id),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('jumio' in data) {
    return {
      ...typeNameColumn,
      jumio: {
        '!type': 'jumio',
        value: {
          id: getReadonlyListItemId(`${prefix}jumio.id`, 'ID', data?.jumio.id),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('onfido' in data) {
    return {
      ...typeNameColumn,
      onfido: {
        '!type': 'onfido',
        value: {
          id: getReadonlyListItemId(`${prefix}onfido.id`, 'ID', data?.onfido.id),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('trulioo' in data) {
    return {
      ...typeNameColumn,
      trulioo: {
        '!type': 'trulioo',
        value: {
          id: getReadonlyListItemId(`${prefix}trulioo.id`, 'ID', data?.trulioo.id),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  exhaustiveCheck(data['!type']);

  return typeNameColumn;
}

export function getRegistrationProfileCipOfacEvidenceVendorDataListItems(data: IxcRegistrationV1CipOfacEvidenceVendorData | undefined, prefix = '') {
  const typeNameColumn = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixcRegistrationV1',
      oneOfName: 'IxcRegistrationV1CipOfacEvidenceVendorData',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return typeNameColumn;
  }

  if ('interchange' in data) {
    return {
      ...typeNameColumn,
      interchange: {
        '!type': 'interchange',
        value: {
          matchCount: getReadonlyListItemNumber(
            `${prefix}interchange.matchCount`,
            'Match Count',
            getNumberOrUndefinedFromNullable(data.interchange, 'matchCount'),
          ),
          screenedAt: getReadonlyListItemDate(`${prefix}interchange.screenedAt`, 'Screened At', data.interchange.screenedAt, 'timestamp_iso_ms'),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('idology' in data) {
    return {
      ...typeNameColumn,
      idology: {
        '!type': 'idology',
        value: {
          id: getReadonlyListItemId(`${prefix}idology.id`, 'ID', data?.idology.id),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('lexisNexis' in data) {
    return {
      ...typeNameColumn,
      lexisNexis: {
        '!type': 'lexisNexis',
        value: {
          id: getReadonlyListItemId(`${prefix}lexisNexis.id`, 'ID', data?.lexisNexis.id),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('plaid' in data) {
    return {
      ...typeNameColumn,
      plaid: {
        '!type': 'plaid',
        value: {
          id: getReadonlyListItemId(`${prefix}plaid.id`, 'ID', data?.plaid.id),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('equifax' in data) {
    return {
      ...typeNameColumn,
      equifax: {
        '!type': 'equifax',
        value: {
          id: getReadonlyListItemId(`${prefix}equifax.id`, 'ID', data?.equifax.id),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('amlPartners' in data) {
    return {
      ...typeNameColumn,
      amlPartners: {
        '!type': 'amlPartners',
        value: {
          id: getReadonlyListItemId(`${prefix}amlPartners.id`, 'ID', data?.amlPartners.id),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('dowJones' in data) {
    return {
      ...typeNameColumn,
      dowJones: {
        '!type': 'dowJones',
        value: {
          id: getReadonlyListItemId(`${prefix}dowJones.id`, 'ID', data?.dowJones.id),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('refinitiv' in data) {
    return {
      ...typeNameColumn,
      refinitiv: {
        '!type': 'refinitiv',
        value: {
          id: getReadonlyListItemId(`${prefix}refinitiv.id`, 'ID', data?.refinitiv.id),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  exhaustiveCheck(data['!type']);

  return typeNameColumn;
}

export function getRegistrationProfileCipIdentityEvidenceListItems(data: IxcRegistrationV1CipIdentityEvidence | undefined, prefix = '') {
  return {
    timestamp: getReadonlyListItemDate(`${prefix}timestamp`, 'Timestamp', data?.timestamp, 'timestamp_iso_ms'),
    outcome: getReadonlyListItemTranslatedString(`${prefix}outcome`, 'Outcome', {
      namespace: 'ixcRegistrationV1',
      enumName: 'IxcRegistrationV1CheckOutcome',
      value: data?.outcome,
    }),
    vendorData: getRegistrationProfileCipIdentityEvidenceVendorDataListItems(data?.vendorData, `${prefix}vendorData`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProfileCipIdentityMinimalEvidenceListItems(
  data: IxcRegistrationV1CipIdentityMinimalEvidence | undefined,
  prefix = '',
) {
  return {
    timestamp: getReadonlyListItemDate(`${prefix}timestamp`, 'Timestamp', data?.timestamp, 'timestamp_iso_ms'),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProfileCipOfacEvidenceListItems(data: IxcRegistrationV1CipOfacEvidence | undefined, prefix = '') {
  return {
    timestamp: getReadonlyListItemDate(`${prefix}timestamp`, 'Timestamp', data?.timestamp, 'timestamp_iso_ms'),
    outcome: getReadonlyListItemTranslatedString(`${prefix}outcome`, 'Outcome', {
      namespace: 'ixcRegistrationV1',
      enumName: 'IxcRegistrationV1CheckOutcome',
      value: data?.outcome,
    }),
    vendorData: getRegistrationProfileCipOfacEvidenceVendorDataListItems(data?.vendorData, `${prefix}vendorData`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProfileCipOfacMinimalEvidenceListItems(data: IxcRegistrationV1CipOfacMinimalEvidence | undefined, prefix = '') {
  return {
    timestamp: getReadonlyListItemDate(`${prefix}timestamp`, 'Timestamp', data?.timestamp, 'timestamp_iso_ms'),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProfileClearingManualProfileApprovalEvidenceListItems(
  data: IxcRegistrationV1ClearingManualProfileApprovalEvidence | undefined,
  prefix = '',
) {
  return {
    timestamp: getReadonlyListItemDate(`${prefix}timestamp`, 'Timestamp', data?.timestamp, 'timestamp_iso_ms'),
    outcome: getReadonlyListItemTranslatedString(`${prefix}outcome`, 'Outcome', {
      namespace: 'ixcRegistrationV1',
      enumName: 'IxcRegistrationV1CheckOutcome',
      value: data?.outcome,
    }),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationClearingProfileEvidenceListItems(data: IxcRegistrationV1ClearingProfileEvidence | undefined, prefix = '') {
  const typeNameColumn = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixcRegistrationV1',
      oneOfName: 'IxcRegistrationV1ClearingProfileEvidence',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return typeNameColumn;
  }

  if ('clearingManualProfileApprovalEvidence' in data) {
    return {
      ...typeNameColumn,
      clearingManualProfileApprovalEvidence: {
        '!type': 'clearingManualProfileApprovalEvidence',
        value: getRegistrationProfileClearingManualProfileApprovalEvidenceListItems(
          data.clearingManualProfileApprovalEvidence,
          `${prefix}clearingManualProfileApprovalEvidence.`,
        ),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  exhaustiveCheck(data['!type']);

  return typeNameColumn;
}

export function getRegistrationProfileEvidenceListItems(data: IxcRegistrationV1ProfileEvidence | undefined, prefix = '') {
  const typeNameColumn = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixcRegistrationV1',
      oneOfName: 'IxcRegistrationV1ProfileEvidence',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return typeNameColumn;
  }

  if ('cipIdentityEvidence' in data) {
    return {
      ...typeNameColumn,
      cipIdentityEvidence: {
        '!type': 'cipIdentityEvidence',
        value: getRegistrationProfileCipIdentityEvidenceListItems(data.cipIdentityEvidence, `${prefix}cipIdentityEvidence.`),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('cipIdentityMinimalEvidence' in data) {
    return {
      ...typeNameColumn,
      cipIdentityMinimalEvidence: {
        '!type': 'cipIdentityMinimalEvidence',
        value: getRegistrationProfileCipIdentityMinimalEvidenceListItems(data.cipIdentityMinimalEvidence, `${prefix}cipIdentityMinimalEvidence.`),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('cipOfacEvidence' in data) {
    return {
      ...typeNameColumn,
      cipOfacEvidence: {
        '!type': 'cipOfacEvidence',
        value: getRegistrationProfileCipOfacEvidenceListItems(data.cipOfacEvidence, `${prefix}cipOfacEvidence.`),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('cipOfacMinimalEvidence' in data) {
    return {
      ...typeNameColumn,
      cipOfacMinimalEvidence: {
        '!type': 'cipOfacMinimalEvidence',
        value: getRegistrationProfileCipOfacMinimalEvidenceListItems(data.cipOfacMinimalEvidence, `${prefix}cipOfacMinimalEvidence.`),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  exhaustiveCheck(data['!type']);

  return typeNameColumn;
}

export function getRegistrationCipIdentityCheckTypeListItems(data: IxcRegistrationV1ProfileCheckTypeCipIdentity | undefined, prefix = '') {
  return {
    evidence: data?.evidence ? getRegistrationProfileCipIdentityEvidenceListItems(data.evidence, `${prefix}evidence.`) : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationCipIdentityMinimalCheckTypeListItems(
  data: IxcRegistrationV1ProfileCheckTypeCipIdentityMinimal | undefined,
  prefix = '',
) {
  return {
    evidence: data?.evidence ? getRegistrationProfileCipIdentityMinimalEvidenceListItems(data.evidence, `${prefix}evidence.`) : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationCipOfacCheckTypeListItems(data: IxcRegistrationV1ProfileCheckTypeCipOfac | undefined, prefix = '') {
  return {
    evidence: data?.evidence ? getRegistrationProfileCipOfacEvidenceListItems(data.evidence, `${prefix}evidence.`) : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationCipOfacMinimalCheckTypeListItems(data: IxcRegistrationV1ProfileCheckTypeCipOfacMinimal | undefined, prefix = '') {
  return {
    evidence: data?.evidence ? getRegistrationProfileCipOfacMinimalEvidenceListItems(data.evidence, `${prefix}evidence.`) : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationClearingManualProfileApprovalCheckTypeListItems(
  data: IxcRegistrationV1ProfileCheckTypeClearingManualProfileApproval | undefined,
  prefix = '',
) {
  return {
    evidence: data?.evidence
      ? getRegistrationProfileClearingManualProfileApprovalEvidenceListItems(data.evidence, `${prefix}clearingManualProfile.evidence.`)
      : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProfileCheckTypeListItems(data: IxcRegistrationV1ProfileCheckType | undefined, prefix = '') {
  const typeNameColumn = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixcRegistrationV1',
      oneOfName: 'IxcRegistrationV1ProfileCheckType',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return typeNameColumn;
  }

  if ('cipIdentity' in data) {
    return {
      ...typeNameColumn,
      cipIdentity: {
        '!type': 'cipIdentity',
        value: getRegistrationCipIdentityCheckTypeListItems(data.cipIdentity, `${prefix}cipIdentity.`),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('cipIdentityMinimal' in data) {
    return {
      ...typeNameColumn,
      cipIdentityMinimal: {
        '!type': 'cipIdentityMinimal',
        value: getRegistrationCipIdentityMinimalCheckTypeListItems(data.cipIdentityMinimal, `${prefix}cipIdentityMinimal.`),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('cipOfac' in data) {
    return {
      ...typeNameColumn,
      cipOfac: {
        '!type': 'cipOfac',
        value: getRegistrationCipOfacCheckTypeListItems(data.cipOfac, `${prefix}cipOfac.`),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('cipOfacMinimal' in data) {
    return {
      ...typeNameColumn,
      cipOfacMinimal: {
        '!type': 'cipOfacMinimal',
        value: getRegistrationCipOfacMinimalCheckTypeListItems(data.cipOfacMinimal, `${prefix}cipOfacMinimal.`),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('clearingManualProfileApproval' in data) {
    return {
      ...typeNameColumn,
      clearingManualProfileApproval: {
        '!type': 'clearingManualProfileApproval',
        value: getRegistrationClearingManualProfileApprovalCheckTypeListItems(
          data?.clearingManualProfileApproval,
          `${prefix}clearingManualProfileApproval.`,
        ),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  exhaustiveCheck(data['!type']);

  return typeNameColumn;
}

export function getRegistrationProfileCheckListItems(data: IxcRegistrationV1ProfileCheck | undefined, prefix = '') {
  return {
    checkId: getReadonlyListItemId(`${prefix}.checkId`, 'Check ID', data?.checkId),
    checkType: getRegistrationProfileCheckTypeListItems(data?.checkType, `${prefix}.checkType`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProfileVersionListItems(data: IxcRegistrationV1ProfileVersion | undefined, prefix = '') {
  return {
    profileVersionId: getReadonlyListItemId(`${prefix}.profileVersionId`, 'Profile Version ID', data?.profileVersionId),
    profile: getRegistrationProfileTypeListItems(data?.profile, `${prefix}profile.`),
    checks: data?.checks?.map((check) => getRegistrationProfileCheckListItems(check, `${prefix}checks.`)) ?? [],
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProfileVersionStateListItems(
  data: IxcRegistrationV1ProfileVersionState | undefined,
  currentProfileVersionId: string | undefined,
  pendingProfileVersionId: string | undefined,
  linkContext: ReplaceValuesAndMakeOptional<IxcRegistrationV1ProfileVersionKeys, string, boolean> = {
    profileVersionId: false,
    profileId: false,
    programId: true,
  },
) {
  return {
    metadata: getJ5StateMetadataListItems(data?.metadata),
    profileVersionId: getReadonlyListItemId(
      'profileVersionId',
      'Profile Version ID',
      data?.profileVersionId,
      linkContext.profileVersionId ? getRegistrationProfileDetailPath(data?.profileId, data?.profileVersionId) : undefined,
    ),
    profileId: getReadonlyListItemId(
      'profileId',
      'Profile ID',
      data?.profileId,
      linkContext.profileId ? getRegistrationProfileDetailPath(data?.profileId) : undefined,
    ),
    programId: getReadonlyListItemId(
      'programId',
      'Program ID',
      data?.programId,
      linkContext.programId ? getProgramDetailPath(data?.programId) : undefined,
    ),
    status: getReadonlyListItemTranslatedString('status', 'Status', {
      namespace: 'ixcRegistrationV1',
      enumName: 'IxcRegistrationV1ProfileVersionStatus',
      value: data?.status,
    }),
    statusPill: getReadonlyListItemPill('statusPill', 'Status', data?.status ? <ProfileVersionStatusPill status={data.status} /> : null),
    statusPillWithHistorical: getReadonlyListItemPill(
      'statusPillWithHistorical',
      'Status',
      data?.status ? (
        <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
          {data.profileVersionId && data.profileVersionId !== pendingProfileVersionId && data.profileVersionId !== currentProfileVersionId ? (
            <Pill variant="dark">Historical</Pill>
          ) : null}
          <ProfileVersionStatusPill status={data.status} />
        </HorizontalStack>
      ) : null,
    ),
    data: {
      profile: getRegistrationProfileTypeListItems(data?.data?.profile, 'data.profile.'),
    },
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProfileVersionListItemSummary(
  data: IxcRegistrationV1ProfileVersionState | undefined,
  currentProfileVersionId: string | undefined,
  pendingProfileVersionId: string | undefined,
) {
  const listItems = getRegistrationProfileVersionStateListItems(data, currentProfileVersionId, pendingProfileVersionId);
  const groups: ReadonlyListGroup[] = [
    {
      id: 'versionMain',
      items: [
        { ...listItems.statusPillWithHistorical, title: 'Version Status' },
        { ...listItems.profileVersionId, title: 'Version ID' },
        listItems.metadata.createdAt,
        listItems.metadata.updatedAt,
      ],
    },
  ];

  switch (data?.data?.profile?.['!type']) {
    case 'individual': {
      groups.push(...getRegistrationIndividualProfileListItemSummary(data.data.profile.individual));

      break;
    }
    case 'organization': {
      groups.push(...getRegistrationOrganizationProfileListItemSummary(data.data.profile.organization));
      break;
    }
    default:
      break;
  }

  return groups;
}
